import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Spade, Users, Coins, Crown, Plus, Zap } from "lucide-react";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageTitle, Loader, Empty, GoldButton } from "../components/UI";
import { useEventStream } from "../lib/sse";

export default function ChallengePage() {
  const { player, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/challenge").then(({ data }) => setData(data)).catch(() => setData({ forming: [], live: [], completed: [], tiers: [] }));
    if (player) api.get("/challenge/mine").then(({ data }) => setMine(data.tables)).catch(() => setMine([]));
  }, [player]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEventStream("/live/stream", (ev) => { if (ev?.type?.startsWith?.("table.")) load(); });

  const create = async (tier) => {
    setBusy(true);
    try {
      const { data } = await api.post("/challenge", { tier });
      toast.success(`Table created — ${data.table.public_id}`);
      await refresh(); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <Loader label="Loading Challenge…" />;

  return (
    <Page>
      <PageTitle eyebrow="Individual Discipline · Live · Virtual Points"
        title="Challenge"
        subtitle="Poker-psychology prediction. Build a secret coupon, then BET, CALL or FOLD against the table. Bluff everyone into folding and take the pot — even with a losing coupon. VP stakes only, no monetary value." />

      {player && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="challenge-create">
          {data.tiers.map((t) => (
            <GoldButton key={t.tier} data-testid={`create-table-${t.tier}`} disabled={busy} onClick={() => create(t.tier)}>
              <Plus className="w-4 h-4" /> Create {t.tier.replace(/_/g, " ")} ({t.buy_in} VP)
            </GoldButton>
          ))}
        </div>
      )}

      {player && (
        <Section title="My Tables" testid="challenge-mine" items={mine} empty="You have not joined any tables yet." />
      )}
      <Section title="Open / Forming" icon={<Zap className="w-5 h-5 text-[#E5B842]" />} testid="challenge-forming"
        items={data.forming} empty="No forming tables — new ones open continuously." joinable player={player} onChange={load} />
      <Section title="Live Tables" testid="challenge-live" items={data.live} empty="No live tables right now." />
      <Section title="Recent Results" icon={<Crown className="w-5 h-5 text-[#E5B842]" />} testid="challenge-completed" items={data.completed} empty="No completed tables yet." />
    </Page>
  );
}

function Section({ title, icon, testid, items, empty, joinable, player, onChange }) {
  return (
    <section data-testid={testid} className="mb-8">
      <h2 className="flex items-center gap-2 font-heading text-xl font-bold uppercase tracking-tight text-slate-50 mb-3">{icon}{title}</h2>
      {items.length === 0 ? <Empty label={empty} /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((t) => <TableCard key={t.id} t={t} joinable={joinable} player={player} onChange={onChange} />)}
        </div>
      )}
    </section>
  );
}

function TableCard({ t, joinable, player, onChange }) {
  const [busy, setBusy] = useState(false);
  const join = async (e) => {
    e.preventDefault(); e.stopPropagation(); setBusy(true);
    try { await api.post(`/challenge/${t.id}/join`); toast.success(`Joined ${t.public_id}`); onChange && onChange(); }
    catch (err) { toast.error(err?.response?.data?.detail?.message || apiErr(err)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`table-${t.id}`} className="rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
      <Link to={`/challenge/${t.id}`} className="block">
        <div className="flex items-center justify-between">
          <span className="font-heading font-black uppercase tracking-wide text-fuchsia-300"><Spade className="w-4 h-4 inline mr-1 -mt-1" />{t.tier.replace(/_/g, " ")}</span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{t.status}</span>
        </div>
        <div className="font-mono text-xs text-slate-400 mt-1">{t.public_id}{t.showcase ? " · SHOWCASE" : ""}</div>
        <div className="grid grid-cols-3 gap-2 mt-3 text-center">
          <Mini icon={<Coins className="w-3 h-3" />} label="Buy-in" value={t.buy_in} />
          <Mini icon={<Users className="w-3 h-3" />} label="Seats" value={`${t.players}/${t.max_players}`} />
          <Mini icon={<Crown className="w-3 h-3" />} label="Pot" value={t.pot} />
        </div>
        {t.winners && t.winners.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#2b2512] text-sm text-[#E5B842] font-bold truncate">
            <Crown className="w-4 h-4 inline" /> {t.winners.map((w) => w?.display_name).join(", ")}{t.foldout ? " (fold-out)" : t.split ? " (split)" : ""}
          </div>
        )}
      </Link>
      {joinable && player && t.status === "FORMING" && (
        <button data-testid={`join-${t.id}`} disabled={busy} onClick={join}
          className="w-full mt-3 px-4 py-2 rounded-lg font-heading font-bold uppercase text-sm bg-[#E5B842] text-[#000000] hover:bg-[#f0c85a] disabled:opacity-50" style={{ transition: "background-color 0.15s ease" }}>
          {busy ? "Joining…" : `Join · ${t.buy_in} VP`}
        </button>
      )}
    </div>
  );
}

function Mini({ icon, label, value }) {
  return (
    <div className="rounded-lg bg-[#000000] border border-[#2b2512] py-2">
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-mono flex items-center justify-center gap-0.5">{icon}{label}</div>
      <div className="font-heading font-black text-slate-100 tabular-nums text-sm">{value}</div>
    </div>
  );
}
